// @flow
import React, {useState, useEffect, useMemo} from 'react';
import Box from 'sail/Box';
import {TextInput} from 'sail/Input';
import ContextualLayer from 'sail/Layer/ContextualLayer';
import Card from 'sail/Card';
import Spinner from 'sail/Spinner';
import {useAnalytics, useSearch, useLocation} from 'docs/data';
import type {SearchResult, ViewHistoryEntry} from 'docs/data';
import {defineMessages, useIntl} from 'react-intl';
import {SearchResultList} from './SearchResultList';
import './Search.scss';

const messages = defineMessages({
  searchDocumentation: {
    id: 'docsShell.search.searchDocumentation',
    description: 'Placeholder in search bar',
    defaultMessage: 'Search documentation...',
  },
  home: {
    id: 'docsShell.search.homeBreadcrumb',
    description: 'Label for home in search breadcrumb',
    defaultMessage: 'Home › {breadcrumb}',
  },
});

// Read http://go/docs-on-docs-search to view high level documentation on how Algolia indexes results
// which is what this component queries.

const getSearchBarIcon = (isLoading, isFocused) => {
  if (isLoading) {
    return {
      'aria-label': 'Loading',
      content: <Spinner className="SearchInput-spinner" />,
    };
  } else if (!isFocused) {
    return {
      'aria-label': 'Forward slash keyboard shortcut',
      content: <kbd className="SearchInput-kbd">/</kbd>,
    };
  } else {
    return null;
  }
};

// Focusing on the text input adds a blue border.
// We increase the width of the search results when the input is
// focused so that the widths are the same.
const INPUT_FOCUS_BORDER_WIDTH = 3;

const formatRecentlyViewed = (
  recentlyViewed: Array<ViewHistoryEntry>,
  intl,
): Array<SearchResult> => {
  return recentlyViewed
    .filter(({url, title}) => {
      // Don't show the homepage in recently viewed
      return url !== '/docs' && title;
    })
    .map((entry) => {
      // TODO(matv): Clean up this function after algolia crawler and recently-viewed cookie both
      // are updated to have clean breadcrumbs, which will obviate the need to perform this logic.
      let breadcrumbs = entry.breadcrumbs
        ? entry.breadcrumbs.join(' › ')
        : intl.formatMessage(messages.home, {breadcrumb: entry.title});

      if (entry.url.includes('/integration-builder')) {
        breadcrumbs = 'Integration Builder';
        const parts = entry.url.split('/');
        const product = parts[parts.length - 2];
        if (product) {
          const formattedProduct =
            product[0].toUpperCase() + product.substring(1);
          breadcrumbs += ` › ${formattedProduct}`;
        }
      }
      return {
        title: entry.title,
        url: entry.url,
        id: entry.url,
        tags: ['docs', 'recent-views'],
        breadcrumbs,
        type: 'doc',
        isExternal: false,
      };
    });
};

type SearchProps = {|
  isInputEndIconVisible?: boolean,
  onResultClick?: () => void,
|};

export const Search = ({
  isInputEndIconVisible = true,
  onResultClick,
}: SearchProps = {}) => {
  const {
    query,
    results: queriedResults,
    isLoading,
    loadSearchResults,
  } = useSearch();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  // https://stackoverflow.com/questions/46240647/react-how-to-force-a-function-component-to-render
  const [, updateState] = useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  const contextRef: {current?: null | HTMLDivElement} = React.useRef();
  const inputRef: {current?: null | TextInput} = React.useRef();

  const intl = useIntl();
  const {trackAction} = useAnalytics();

  const {location, pushLocation} = useLocation();
  const {getViewHistory} = useAnalytics();
  const recentlyViewedDocs = useMemo(
    () => formatRecentlyViewed(getViewHistory(), intl),
    // TODO: use correct dependencies
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [location],
  );

  const shortcuts = [
    {title: 'Suggested', type: 'search-result-heading'},
    {
      title: 'API keys',
      id: 'test-api-keys-shortcut',
      type: 'shortcut',
      queryShortcut: 'api key',
      isExternal: false,
    },
    {
      title: 'Test cards',
      id: 'test-cards-shortcut',
      type: 'shortcut',
      queryShortcut: 'test cards',
      isExternal: false,
    },
  ];

  const recentlyViewedDocsWithTitle =
    recentlyViewedDocs.length > 0
      ? [
          {title: 'Recently viewed', type: 'search-result-heading'},
          ...recentlyViewedDocs,
        ]
      : [];

  const initialStateDisplayResults = [
    ...recentlyViewedDocsWithTitle,
    ...shortcuts,
  ];

  // Show the 'results' card when focused. In the scenario where the user is
  // typing a query, there are no results yet and we are loading, do not show
  // the card.
  const showResultsCard = isFocused
    ? !isLoading || (isLoading && queriedResults.length !== 0)
    : false;

  const showInitialState = !query || !query.trim();

  const results = showInitialState
    ? initialStateDisplayResults
    : queriedResults;

  const handleInvocationKeys = (event: KeyboardEvent) => {
    // Focus in the search input when '/' or 's' or 'cmd+k' are pressed.
    if (
      (event.keyCode === 191 && !event.metaKey) ||
      event.keyCode === 83 ||
      (event.metaKey && event.keyCode === 75)
    ) {
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLSelectElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement?.getAttribute('role') === 'textbox'
      ) {
        return;
      }

      if (inputRef && inputRef.current && inputRef.current.handleFocus) {
        event.preventDefault();
        // $FlowExpectedError Flow thinks current.handleFocus might not exist
        inputRef.current.handleFocus();
      }
    }
  };

  const getSelectedResult = () => {
    return results[selectedIndex];
  };

  const handleResultsNavKeys = (event: KeyboardEvent) => {
    if (results.length === 0) {
      return;
    }

    if (event.keyCode === 40) {
      // down
      if (selectedIndex < results.length - 1) {
        let newSelectedIndex = selectedIndex + 1;
        if (results[newSelectedIndex]?.type === 'search-result-heading') {
          newSelectedIndex++;
        }
        setSelectedIndex(Math.min(newSelectedIndex, results.length - 1));
      }
      event.preventDefault();
      return;
    }
    if (event.keyCode === 38) {
      // up
      if (selectedIndex > 0) {
        let newSelectedIndex = selectedIndex - 1;
        if (results[newSelectedIndex]?.type === 'search-result-heading') {
          newSelectedIndex--;
        }
        setSelectedIndex(Math.max(0, newSelectedIndex));
      }
      event.preventDefault();
      return;
    }
    if (event.keyCode === 13) {
      // enter
      const result = getSelectedResult();
      if (result) {
        const el = document.querySelector('.SearchResult-selected');
        if (el != null) {
          el.focus();
          // TODO (matv): Think about avoiding doing this click here. The problem currently is that
          // the copyable cards maintain internal state when the item is copied and shows
          // a "Copied" toast message. It is hard to extract that state up into this component
          // without reducing the code readability. Another option would be to have the key up
          // and key down focus the anchor tags in the results, and then when enter is pressed it
          // will by default perform a click. The problem with this is it degrades the user expierence -
          // since focus is shifted from the text input to the selected result, the cursor will move.
          el.click();
          event.preventDefault();
        }
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (showResultsCard) {
      if (event.keyCode === 27) {
        // Hide card on esc key
        setIsFocused(false);
        if (inputRef && inputRef.current && inputRef.current.blur) {
          event.preventDefault();
          // eslint-disable-next-line no-unused-expressions
          inputRef.current?.blur();
        }
        return;
      }
      handleResultsNavKeys(event);
      return;
    }
    handleInvocationKeys(event);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    };
  });

  const handleInputChange = async (searchQuery: string) => {
    const oldSelectedResult = getSelectedResult();
    const newResults = await loadSearchResults(searchQuery);

    const differentSelectedResult =
      oldSelectedResult &&
      oldSelectedResult.id &&
      newResults[selectedIndex] &&
      newResults[selectedIndex].id === oldSelectedResult.id;

    if (!differentSelectedResult) {
      // When the user types, reset the selected search result to be the first one
      // only if the result they had highlighted has not changed.
      setSelectedIndex(0);
    }
  };

  const inputEndIcon = getSearchBarIcon(isLoading, isFocused);
  let inputEnd;
  if (isInputEndIconVisible && inputEndIcon) {
    inputEnd = inputEndIcon;
  }

  let searchResultsWidth = 0;
  if (contextRef.current) {
    searchResultsWidth =
      contextRef.current.getBoundingClientRect().width +
      INPUT_FOCUS_BORDER_WIDTH * 2;
  }

  const onSearchResultClick = async (result: SearchResult) => {
    // TODO(matv): Consider using Algolia's analytics library for richer analytics.
    if (result.type === 'copyable') {
      trackAction('search-copy', {
        query,
        resultTitle: result.title,
        resultCopied: result.id,
      });
    }
    if (result.type === 'shortcut') {
      await loadSearchResults(result.queryShortcut);
      setSelectedIndex(0);
      // Since a click was performed, we lost focus on the input, so refocus
      if (inputRef && inputRef.current && inputRef.current.handleFocus) {
        inputRef.current.handleFocus();
      }
      trackAction('search-shortcut', {
        resultTitle: result.title,
      });
    }
    if (result.url) {
      trackAction('search', {
        query,
        title: result.title,
        href: result.url,
        tags: result.tags,
      });

      setIsFocused(false);
      // This will reset text in the input field
      loadSearchResults('');

      if (result.type === 'doc') {
        // This will use the history API and avoid doing a full page refresh
        pushLocation(result.url);
      } else if (result.isExternal) {
        window.open(result.url, '_blank');
      }

      if (onResultClick) {
        onResultClick();
      }
    }
  };

  return (
    <Box flexGrow={1} className="Search">
      <div ref={contextRef} id="Search-inputWrapper">
        <TextInput
          size="large"
          type="search"
          width="maximized"
          placeholder={intl.formatMessage(messages.searchDocumentation)}
          value={query}
          onChange={(...args) => {
            handleInputChange(...args);
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          ref={inputRef}
          end={inputEnd}
        />
      </div>
      {contextRef.current && showResultsCard ? (
        <ContextualLayer
          context={{element: contextRef.current}}
          contextMeetingPoint="bottomleft"
          layerMeetingPoint="topleft"
          offsetY={10}
          offsetX={-INPUT_FOCUS_BORDER_WIDTH}
          onClickOutside={(e) => {
            if (
              contextRef.current &&
              e.target instanceof HTMLElement &&
              e.target.closest(`#${contextRef.current.id}`)
            ) {
              return 'not-handled';
            }
            setIsFocused(false);
            setSelectedIndex(0);
            return 'handled';
          }}
          onResizeWindow={(e) => {
            // We need to recompute the width of the ContextualLayer whenever
            // our window is resized because contextRef.current has dynamic width.
            forceUpdate();
            return 'handled';
          }}
          style={{
            width: `${searchResultsWidth}px`,
          }}
        >
          <Card
            radius="all"
            shadow="small"
            className="Search-overlayCard"
            style={{
              width: `${searchResultsWidth}px`,
            }}
          >
            <SearchResultList
              results={results}
              selectedIndex={
                results[selectedIndex]?.type === 'search-result-heading'
                  ? selectedIndex + 1
                  : selectedIndex
              }
              setSelectedIndex={setSelectedIndex}
              showRecentlyViewed={showInitialState}
              onSearchResultClick={onSearchResultClick}
            />
          </Card>
        </ContextualLayer>
      ) : null}
    </Box>
  );
};
